import { useState, useEffect, useRef } from 'react';
import MessageContent from './MessageContent';

/**
 * StreamingText - Efecto typewriter para respuestas de DamyxIA
 * Revela el contenido progresivamente palabra por palabra.
 * Solo se activa para mensajes nuevos (isNew=true).
 * Una vez completado, renderiza el contenido completo normalmente.
 */
const StreamingText = ({ content, isNew = false }) => {
  const [displayedContent, setDisplayedContent] = useState(isNew ? '' : content);
  const [isComplete, setIsComplete] = useState(!isNew);
  const intervalRef = useRef(null);
  const indexRef = useRef(0);

  useEffect(() => {
    if (!isNew || !content) {
      setDisplayedContent(content);
      setIsComplete(true);
      return;
    }

    // Reset for new content
    indexRef.current = 0;
    setDisplayedContent('');
    setIsComplete(false);

    // Reveal content progressively
    // Speed: ~4 chars per tick, 20ms interval = ~200 chars/sec (visible like ChatGPT)
    const CHARS_PER_TICK = 4;
    const TICK_MS = 20;

    intervalRef.current = setInterval(() => {
      indexRef.current += CHARS_PER_TICK;
      
      if (indexRef.current >= content.length) {
        setDisplayedContent(content);
        setIsComplete(true);
        clearInterval(intervalRef.current);
      } else {
        // Find a safe break point (don't cut in the middle of a word or markdown)
        let breakIdx = indexRef.current;
        // Try to break at a space or newline
        while (breakIdx < content.length && content[breakIdx] !== ' ' && content[breakIdx] !== '\n') {
          breakIdx++;
        }
        indexRef.current = breakIdx;
        setDisplayedContent(content.substring(0, breakIdx));
      }
    }, TICK_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [content, isNew]);

  // When content changes externally (e.g., message edited), sync immediately
  useEffect(() => {
    if (isComplete) {
      setDisplayedContent(content);
    }
  }, [content, isComplete]);

  return (
    <div data-testid="streaming-text">
      <MessageContent content={displayedContent} />
      {!isComplete && (
        <span className="inline-block w-2 h-4 bg-indigo-400 animate-pulse ml-0.5 align-text-bottom rounded-sm" />
      )}
    </div>
  );
};

export default StreamingText;
